import { DateTime } from 'luxon';
import { Link } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import { useTenant } from '@/hooks/use-tenant';
import { useCampaigns } from '@/hooks/use-campaigns';
import { campaignDayLabel } from '@/lib/home-dates';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';
import { formatCurrency, formatNumber } from '@/components/metrics/formatters';
import { HomeCardLink } from '@/components/home/home-card-link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const RECENT_CAMPAIGNS_LIMIT = 3;

/**
 * "Últimas campanhas" (HOME-08): the 3 most recently sent campaigns (backend
 * order: `launchDateTime desc`) with send day/time, CTR, revenue and
 * orders; "—" when the campaign hides its data. Each row opens the campaign.
 */
export function RecentCampaignsCard({
  currency,
}: {
  currency: string | undefined;
}) {
  const { LL, locale } = useI18nContext();
  const tenant = useTenant();
  const campaigns = useCampaigns(
    { hasBeenSent: 'true' },
    RECENT_CAMPAIGNS_LIMIT,
    0
  );
  const base = `/${tenant.slug}/campaigns`;

  const body = () => {
    if (campaigns.isError) {
      return (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-muted-foreground">
            {LL.home.recentCampaigns.loadError()}
          </p>
          <Button
            type="button"
            variant="outline"
            className={mobileLayer.touch}
            onClick={() => void campaigns.refetch()}
          >
            {LL.home.recentCampaigns.retry()}
          </Button>
        </div>
      );
    }

    if (!campaigns.data) {
      return (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      );
    }

    if (campaigns.data.data.length === 0) {
      return (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-muted-foreground">
            {LL.home.recentCampaigns.empty()}
          </p>
          <Button asChild className={mobileLayer.touch}>
            <Link to={`${base}/create`}>
              {LL.home.recentCampaigns.create()}
            </Link>
          </Button>
        </div>
      );
    }

    return (
      <ul className="flex flex-col divide-y">
        {campaigns.data.data.map((campaign) => {
          const hidden = campaign.analytics.dataSufficiency.shouldHideData;
          const ctr = hidden
            ? '—'
            : new Intl.NumberFormat(locale, {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              }).format(campaign.analytics.ctr);
          return (
            <li key={campaign.id}>
              <Link
                to={`${base}/${campaign.id}`}
                className={cn(
                  'flex items-center justify-between gap-3 py-3',
                  mobileLayer.touch
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">
                    {campaign.name}
                  </span>
                  <span
                    className="block text-xs text-muted-foreground"
                    data-campaign-when
                  >
                    {LL.home.recentCampaigns.when({
                      day: campaignDayLabel(campaign.launchDateTime, locale),
                      time: DateTime.fromISO(campaign.launchDateTime).toFormat(
                        'HH:mm'
                      ),
                      ctr,
                    })}
                  </span>
                </span>
                <span className="shrink-0 text-right text-sm tabular-nums">
                  <span className="block font-medium" data-campaign-revenue>
                    {hidden
                      ? '—'
                      : formatCurrency(
                          campaign.analytics.revenue,
                          locale,
                          currency ?? 'BRL'
                        )}
                  </span>
                  <span
                    className="block text-xs text-muted-foreground"
                    data-campaign-orders
                  >
                    {hidden
                      ? '—'
                      : formatNumber(
                          campaign.analytics.paidOrderCount,
                          locale
                        )}{' '}
                    {LL.home.recentCampaigns.orders()}
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Card data-home-recent-campaigns className="flex flex-col">
      <CardContent className="flex flex-1 flex-col p-5 max-md:p-4">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {LL.home.recentCampaigns.title()}
        </p>
        {body()}
        <HomeCardLink to={base} className="mt-auto pt-1.5">
          {LL.home.recentCampaigns.viewAll()}
        </HomeCardLink>
      </CardContent>
    </Card>
  );
}
